import React, { useEffect, useState } from "react";
import LogConsistency from "./LogConsistency";
import StreakCard from "./StreakCard";
import CalorieComparison from "./CalorieComparison";
import WaterIntakeChart from "./WaterIntakeChart";
import SleepChart from "./SleepChart";
import GutHealthChart from "./GutHealthChart";
import { GutHealthDayLog, PoopEntry } from "./GutHealthDetailPopup";
import DailyStepsChart from "./DailyStepsChart";
import ActivityTrendChart from "./ActivityTrendChart";
import BloodPressureChart from "./BloodPressureChart";
import WeekdaysVsWeekendsAdherence from "./WeekdaysVsWeekendsAdherence";
import MealTimingConsistency from "./MealTimingConsistency";
import MealSkipTrends from "./MealSkipTrends";
import MealWiseMacros, { DayHeader, MealCell, MealRow as MacroRow } from "./MealWiseMacros";
import WeeklyMacroIntakeDistribution, {
  MacroTab,
  IntakeVsGoalDay,
  TimeSplitDay,
} from "./WeeklyMacroIntakeDistribution";
import { Status, MealRow as TimingRow } from "./MealTimingConsistency";
import { DayLog, Meal, FoodItem } from "./WeeklyMacroDetailPopup";
import { getInsightsWeeklyData } from "../../../../../services/clientDetails";

// ── API response types ────────────────────────────────────────────────────
interface DayStatus {
  date: string;
  status: string; // "complete" | "partial" | "future" | "none" | ...
}

interface WeeklyInsightsData {
  period_start: string;
  period_end: string;
  log_consistency: {
    overall: number;
    meals: number;
    water: number;
    sleep: number;
    gut: number;
    last_week_overall: number;
    change_points: number;
  };
  daily_streak: {
    current_streak: number;
    longest_streak: number;
    days_this_week: number;
    days: DayStatus[];
  };
  calorie_comparison: {
    daily: { date: string; calories_intake: number; calories_burned: number }[];
    this_week_total_intake: number;
    last_week_total_intake: number;
    change_percent: number | null;
  };
  water: {
    daily: {
      date: string;
      water_intake_ml: number;
      goal_liters: number;
      percent_of_goal: number;
    }[];
    total_ml: number;
    avg_liters: number;
    change_percent: number | null;
  };
  sleep: {
    this_week: { date: string; sleep_hours: number; mood: string | null }[];
    last_week: { date: string; sleep_hours: number; mood: string | null }[];
    avg_nap_hours: number;
  };
  gut_health: {
    distribution_percent: {
      excellent: number;
      good: number;
      satisfactory: number;
      hard: number;
      poor: number;
      no_poop: number;
    };
    change_percent: number | null;
    daily_cycle_detail: {
      date: string;
      count: number;
      cycles: {
        ampm: "AM" | "PM";
        mood: string; // "Excellent" | "Good" | "Satisfactory" | "Hard" | "Poor" | "No poop"
        comment: string;
        cycleno: number;
        cycle_id: string;
        cycle_hours: number;
        cycle_minutes: number;
      }[];
    }[];
  };
  activity: {
    daily_steps: { date: string; steps: number }[];
    total_steps: number;
    activity_trend: { date: string; minutes: number }[];
    avg_duration_minutes: number;
  };
  blood_pressure: { date: string; systolic: number | null; diastolic: number | null }[];
  meal_skip_trend: {
    daily: { date: string; skipped_count: number; skipped_meals: string[] }[];
    avg_skips_per_day: number;
  };
  weekday_vs_weekend: {
    calories: { weekday_avg: number; weekend_avg: number; difference: number };
    protein:  { weekday_avg: number; weekend_avg: number; difference: number };
    carbs:    { weekday_avg: number; weekend_avg: number; difference: number };
    fat:      { weekday_avg: number; weekend_avg: number; difference: number };
  };
  meal_timing: {
    meal_types: string[];
    days: {
      date: string;
      meals: {
        meal_type: string;
        status: string; // "on_time" | "not_on_time" | "skipped" | "not_logged" | "not_applicable"
        planned_time: string | null;
        actual_time: string | null;
        delay_minutes: number | null;
        plan_name: string;
        plan_code: string;
        meal_plan_type: string;
      }[];
      unplanned_count: number;
      unplanned_meals: {
        meal_type: string;
        actual_time: string;
        calories: number;
      }[];
    }[];
    on_time_tolerance_minutes: number;
  };
  meal_plans: { date: string; plan_id: number; plan_name: string; plan_code: string; plan_type: string }[];
  meal_macros: {
    meal_type: string;
    days: {
      date: string;
      state: string; // "logged" | "not_logged" | "skipped"
      actual: {
        calories: number;
        protein: number;
        carbs: number;
        fat: number;
        gl: number;
        planned_time: string;
        actual_time: string;
      } | null;
      goal: {
        calories: number;
        protein: number;
        carbs: number;
        fat: number;
      } | null;
    }[];
  }[];
  before_after_6pm: {
    daily: {
      date: string;
      before_6pm: { calories: number; protein: number; carbs: number; fat: number; gl: number };
      after_6pm:  { calories: number; protein: number; carbs: number; fat: number; gl: number };
    }[];
  };
  calorie_goal: {
    daily: {
      date: string;
      actual: number;
      goal: number;
      meals: {
        meal_type: string;
        actual_time: string | null;
        calories: number;
        items: {
          name: string;
          quantity: number;
          units: string;
          calories: number;
        }[];
      }[];
      highest_dish: {
        name: string;
        meal_type: string;
        calories: number;
        percent_of_day: number;
      } | null;
    }[];
  };
}

// ── Helpers ───────────────────────────────────────────────────────────────
const DAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const dayLabel = (dateStr: string): string => {
  const d = new Date(dateStr);
  return DAY_LABELS[d.getDay()];
};

const formatDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const mapStatus = (val: string): "none" | "partial" | "logged" => {
  if (val === "complete") return "logged";
  if (val === "partial") return "partial";
  return "none";
};

const formatMealTime = (t?: string | null): string => {
  if (!t) return "";
  const [hh, mm] = t.split(":");
  const h = parseInt(hh, 10);
  const suffix = h >= 12 ? "pm" : "am";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${mm} ${suffix}`;
};

const mapTimingStatus = (val?: string): Status => {
  if (val === "on_time") return "on-time";
  if (val === "not_on_time") return "not-on-time";
  if (val === "skipped") return "skipped";
  return "not-logged";
};

const formatCycleTime = (hours: number, minutes: number, ampm: "AM" | "PM"): string => {
  const h12 = ((hours + 11) % 12) + 1;
  const mm = String(minutes).padStart(2, "0");
  return `${h12}:${mm} ${ampm}`;
};

type PoopStatus = "Excellent" | "Good" | "Satisfactory" | "Hard" | "Poor" | "No poop";
const KNOWN_POOP_STATUSES: PoopStatus[] = [
  "Excellent", "Good", "Satisfactory", "Hard", "Poor", "No poop",
];
const normalizePoopStatus = (raw: string): PoopStatus => {
  const match = KNOWN_POOP_STATUSES.find((s) => s.toLowerCase() === raw?.toLowerCase());
  return match ?? "Satisfactory";
};

type MacroKey = "calories" | "protein" | "carbs" | "fat" | "gl";
const TAB_TO_MACRO: Record<MacroTab, MacroKey> = {
  "Calories":    "calories",
  "Adj Protein": "protein",
  "Carbs":       "carbs",
  "Fat":         "fat",
  "GL":          "gl",
};

interface WeeklyInsightsViewProps {
  userId: number;
  weekStart: Date;
  weekEnd: Date;
  refreshKey?: number;
  onLoadingChange?: (loading: boolean) => void;
}

const WeeklyInsightsView: React.FC<WeeklyInsightsViewProps> = ({
  userId,
  weekStart,
  refreshKey = 0,
  onLoadingChange,
}) => {
  const [data, setData] = useState<WeeklyInsightsData | null>(null);

  useEffect(() => {
    const fetchInsights = async () => {
      setData(null);
      onLoadingChange?.(true);
      try {
        const json: WeeklyInsightsData = await getInsightsWeeklyData(
          userId,
          formatDate(weekStart),
        );
        setData(json);
      } catch (err) {
        console.error("WeeklyInsightsView fetch error:", err);
        setData(null);
      } finally {
        onLoadingChange?.(false);
      }
    };
    fetchInsights();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, weekStart, refreshKey]);

  // ── Derived per-chart data ─────────────────────────────────────────────
  const lc = data?.log_consistency;
  const ds = data?.daily_streak;
  const cc = data?.calorie_comparison;
  const wt = data?.water;
  const sl = data?.sleep;
  const gh = data?.gut_health;
  const act = data?.activity;
  const bp = data?.blood_pressure;
  const skip = data?.meal_skip_trend;

  const weekLogs = ds
    ? (ds.days.map((d) => mapStatus(d.status)) as ("none" | "partial" | "logged")[])
    : undefined;

  const calorieData = cc?.daily.map((d) => ({
    day: dayLabel(d.date),
    intake: d.calories_intake,
    burned: d.calories_burned,
  }));

  const waterData = wt?.daily.map((d) => ({
    day: dayLabel(d.date),
    actual: parseFloat((d.water_intake_ml / 1000).toFixed(2)),
    target: d.goal_liters,
  }));

  const sleepData = sl
    ? sl.this_week.map((tw, i) => ({
        day: dayLabel(tw.date),
        thisWeek: tw.sleep_hours ? parseFloat(tw.sleep_hours.toFixed(1)) : null,
        lastWeek: sl.last_week[i]?.sleep_hours
          ? parseFloat(sl.last_week[i].sleep_hours.toFixed(1))
          : null,
      }))
    : undefined;

  const gutData = gh
    ? [
        { label: "Excellent",    value: gh.distribution_percent.excellent },
        { label: "Good",         value: gh.distribution_percent.good },
        { label: "Satisfactory", value: gh.distribution_percent.satisfactory },
        { label: "Hard",         value: gh.distribution_percent.hard },
        { label: "Poor",         value: gh.distribution_percent.poor },
        { label: "No poop",      value: gh.distribution_percent.no_poop },
      ]
    : undefined;

  const gutPopupDays: GutHealthDayLog[] | undefined = gh?.daily_cycle_detail.map((d) => {
    const date = new Date(d.date);
    const dowLabel = DAY_LABELS[date.getDay()];
    const monthDay = date.toLocaleDateString("en-GB", { day: "numeric", month: "short" }).toUpperCase();
    const entries: PoopEntry[] = d.cycles.map((c) => ({
      status: normalizePoopStatus(c.mood),
      time: formatCycleTime(c.cycle_hours, c.cycle_minutes, c.ampm),
      message: c.comment || undefined,
    }));
    return {
      dayLabel: `${dowLabel.toUpperCase()}, ${monthDay}`,
      status: d.count > 0 ? "logged" : "not-logged",
      entries,
    };
  });

  const stepsData = act?.daily_steps.map((d) => ({
    day: dayLabel(d.date),
    steps: d.steps,
  }));

  const activityData = act?.activity_trend.map((d) => ({
    day: dayLabel(d.date),
    minutes: d.minutes,
  }));

  const bpData = bp?.map((d) => ({
    day: dayLabel(d.date),
    systolic: d.systolic,
    diastolic: d.diastolic,
  }));

  const skipData = skip?.daily.map((d) => ({
    day: dayLabel(d.date),
    skips: d.skipped_count,
    meals: d.skipped_meals,
  }));

  // ── Weekday vs Weekend ──────────────────────────────────────────────────
  const wvw = data?.weekday_vs_weekend;
  const wvwProps = wvw
    ? {
        calories: { weekdays: wvw.calories.weekday_avg, weekends: wvw.calories.weekend_avg },
        protein:  { weekdays: wvw.protein.weekday_avg,  weekends: wvw.protein.weekend_avg },
        carbs:    { weekdays: wvw.carbs.weekday_avg,    weekends: wvw.carbs.weekend_avg },
        fat:      { weekdays: wvw.fat.weekday_avg,      weekends: wvw.fat.weekend_avg },
      }
    : undefined;

  // ── Meal Timing Consistency ─────────────────────────────────────────────
  const mt = data?.meal_timing;
  const timingRows: TimingRow[] | undefined = mt?.meal_types.map((mealType) => {
    const firstPlannedTime = mt.days
      .map((d) => d.meals.find((m) => m.meal_type === mealType)?.planned_time)
      .find((t) => !!t) ?? undefined;
    return {
      meal: mealType,
      time: formatMealTime(firstPlannedTime),
      cells: mt.days.map((d) => {
        const meal = d.meals.find((m) => m.meal_type === mealType);
        return {
          status: mapTimingStatus(meal?.status),
          mealPlanType: meal?.meal_plan_type,
          plannedTime: meal?.planned_time ? formatMealTime(meal.planned_time) : undefined,
        };
      }),
    };
  });
  const unplannedCounts = mt?.days.map((d) => d.unplanned_count);

  // ── Meal-Wise Macros ────────────────────────────────────────────────────
  const mealPlans = data?.meal_plans;
  const mealMacros = data?.meal_macros;

  const mwmDays: DayHeader[] | undefined = mealPlans?.map((p) => ({
    day: dayLabel(p.date),
    tag: p.plan_type,
  }));

  const todayIso = new Date().toISOString().slice(0, 10);
  const mwmRows: MacroRow[] | undefined = mealMacros?.map((meal) => {
    const rowDays: MealCell[] = meal.days.map((d) => {
      if (d.state === "skipped") return { status: "skipped" };
      if (d.state === "logged" && d.actual) {
        return {
          status: "logged",
          time: formatMealTime(d.actual.actual_time),
          cal:  { actual: Math.round(d.actual.calories), goal: Math.round(d.goal?.calories ?? 0) },
          pro:  { actual: Math.round(d.actual.protein),  goal: Math.round(d.goal?.protein ?? 0) },
          carb: { actual: Math.round(d.actual.carbs),    goal: Math.round(d.goal?.carbs ?? 0) },
          fat:  { actual: Math.round(d.actual.fat),      goal: Math.round(d.goal?.fat ?? 0) },
        };
      }
      // "not_applicable" / "na" / "not_planned" — meal isn't part of this day's plan
      if (d.state === "not_applicable" || d.state === "na" || d.state === "not_planned") return { status: "na" };
      // Future date — no data yet
      if (d.date && d.date > todayIso) return { status: "none" };
      // Anything else (e.g. "not_logged") — user didn't log
      return { status: "notLogged" };
    });
    return { meal: meal.meal_type, days: rowDays };
  });

  // ── Weekly Macro Intake & Distribution Sources ──────────────────────────
  const ba = data?.before_after_6pm;
  const cg = data?.calorie_goal;

  // Sum meal_macros actual + goal per day for a given macro key.
  const macroDailyIntakeVsGoal = (
    key: Exclude<MacroKey, "calories" | "gl">,
  ): IntakeVsGoalDay[] | undefined => {
    if (!mealMacros?.length || !mealPlans?.length) return undefined;
    return mealPlans.map((p) => {
      let actual = 0;
      let goal = 0;
      mealMacros.forEach((m) => {
        const dayEntry = m.days.find((dd) => dd.date === p.date);
        if (!dayEntry) return;
        actual += dayEntry.actual?.[key] ?? 0;
        goal += dayEntry.goal?.[key] ?? 0;
      });
      return { day: dayLabel(p.date), actual: Math.round(actual), goal: Math.round(goal) };
    });
  };

  const buildTimeSplit = (key: MacroKey): TimeSplitDay[] | undefined =>
    ba?.daily.map((d) => ({
      day: dayLabel(d.date),
      before6pm: Math.round(d.before_6pm[key]),
      after6pm:  Math.round(d.after_6pm[key]),
    }));

  const calorieIntakeVsGoal: IntakeVsGoalDay[] | undefined = cg?.daily.map((d) => ({
    day: dayLabel(d.date),
    actual: Math.round(d.actual),
    goal: Math.round(d.goal),
  }));

  // GL intake vs goal: goal isn't tracked; use before_after_6pm.gl summed with same value as goal placeholder.
  const glDailySum: IntakeVsGoalDay[] | undefined = ba?.daily.map((d) => {
    const total = (d.before_6pm.gl ?? 0) + (d.after_6pm.gl ?? 0);
    return { day: dayLabel(d.date), actual: Math.round(total), goal: 100 };
  });

  const dataByMacro: Partial<Record<MacroTab, { timeSplit: TimeSplitDay[]; intakeVsGoal: IntakeVsGoalDay[] }>> = {};
  (Object.keys(TAB_TO_MACRO) as MacroTab[]).forEach((tab) => {
    const key = TAB_TO_MACRO[tab];
    const ts = buildTimeSplit(key);
    let ivg: IntakeVsGoalDay[] | undefined;
    if (key === "calories") ivg = calorieIntakeVsGoal;
    else if (key === "gl") ivg = glDailySum;
    else ivg = macroDailyIntakeVsGoal(key as "protein" | "carbs" | "fat");
    if (ts && ivg) dataByMacro[tab] = { timeSplit: ts, intakeVsGoal: ivg };
  });

  // ── Popup day rows ──────────────────────────────────────────────────────
  const popupDays: DayLog[] | undefined = cg?.daily.map((d) => {
    const date = new Date(d.date);
    const dowLabel = DAY_LABELS[date.getDay()];
    const plan = mealPlans?.find((p) => p.date === d.date);
    const monthDay = date.toLocaleDateString("en-GB", { day: "numeric", month: "short" }).toUpperCase();
    return {
      dayShort: dowLabel,
      dayLabel: `${dowLabel.toUpperCase()}, ${monthDay}`,
      mealType: plan?.plan_type ?? "",
      goal: Math.round(d.goal),
      meals: d.meals.map((m): Meal => ({
        name: m.meal_type.toUpperCase(),
        time: formatMealTime(m.actual_time),
        items: m.items.map((it): FoodItem => ({
          name: it.name,
          quantity: `${it.quantity} ${it.units}`.trim(),
          kcal: Math.round(it.calories),
        })),
      })),
    };
  });

  return (
    <>
      {/* Row 1: Log Consistency | Streak | Calorie Comparison */}
      <div key={`w-r1-${refreshKey}`} className="grid grid-cols-9 gap-4 px-6 mb-4 items-stretch">
        <div className="col-span-3 h-full">
          <LogConsistency
            consistency={lc?.overall}
            lastWeekConsistency={lc?.last_week_overall}
            meals={lc?.meals}
            water={lc?.water}
            sleep={lc?.sleep}
            gut={lc?.gut}
          />
        </div>
        <div className="col-span-2 h-full">
          <StreakCard
            streakDays={ds?.current_streak}
            longestStreak={ds?.longest_streak}
            weekLogs={weekLogs}
            weekStartDate={weekStart}
          />
        </div>
        <div className="col-span-4 h-full">
          <CalorieComparison
            data={calorieData}
            vsLastWeekPercent={cc?.change_percent ?? undefined}
          />
        </div>
      </div>

      {/* Row 2: Water | Sleep | Gut Health */}
      <div key={`w-r2-${refreshKey}`} className="grid grid-cols-11 gap-4 px-6 mb-4 items-stretch">
        <div className="col-span-4 h-full">
          <WaterIntakeChart
            data={waterData}
            avgLiters={wt?.avg_liters}
            vsLastWeekPercent={wt?.change_percent ?? undefined}
          />
        </div>
        <div className="col-span-4 h-full">
          <SleepChart data={sleepData} avgNapHours={sl?.avg_nap_hours} />
        </div>
        <div className="col-span-3 h-full">
          <GutHealthChart
            data={gutData}
            vsLastWeekPercent={gh?.change_percent ?? undefined}
            popupDays={gutPopupDays}
          />
        </div>
      </div>

      {/* Row 3: Daily Steps | Activity Trend | Blood Pressure */}
      <div key={`w-r3-${refreshKey}`} className="grid grid-cols-3 gap-4 px-6 mb-4 items-stretch">
        <div className="h-full">
          <DailyStepsChart data={stepsData} />
        </div>
        <div className="h-full">
          <ActivityTrendChart
            data={activityData}
            avgHours={
              act?.avg_duration_minutes != null
                ? parseFloat((act.avg_duration_minutes / 60).toFixed(1))
                : undefined
            }
          />
        </div>
        <div className="h-full">
          <BloodPressureChart data={bpData} />
        </div>
      </div>

      {/* Row 4 */}
      <div key={`w-r4-${refreshKey}`} className="px-6 mb-4">
        <WeekdaysVsWeekendsAdherence {...(wvwProps ?? {})} />
      </div>

      {/* Row 5 */}
      <div key={`w-r5-${refreshKey}`} className="grid grid-cols-5 gap-4 px-6 mb-4 items-stretch">
        <div className="col-span-3 h-full">
          <MealTimingConsistency data={timingRows} unplanned={unplannedCounts} />
        </div>
        <div className="col-span-2 h-full">
          <MealSkipTrends
            data={skipData}
            avgSkips={skip?.avg_skips_per_day}
          />
        </div>
      </div>

      {/* Row 6 */}
      <div key={`w-r6-${refreshKey}`} className="px-6 mb-4">
        <MealWiseMacros days={mwmDays} rows={mwmRows} />
      </div>

      {/* Row 7 */}
      <div key={`w-r7-${refreshKey}`} className="px-6 mb-4">
        <WeeklyMacroIntakeDistribution dataByMacro={dataByMacro} days={popupDays} />
      </div>
    </>
  );
};

export default WeeklyInsightsView;
